/**
 * Settings Page
 * Application settings and configuration
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Settings,
  Database,
  Palette,
  Bell,
  Shield,
  Globe,
  Save,
  RefreshCw,
  Upload,
  FileJson,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Package,
  Link2,
  Brain,
  ArrowLeftRight,
  RotateCcw,
  Loader2,
  Server,
  Image,
  X,
  Sun,
  Moon,
  Monitor,
  Volume2,
  VolumeX,
  BellRing,
  Mail,
  Info,
  AlertTriangle,
  XCircle,
  Plus,
  Edit3,
  ChevronDown,
  ChevronUp,
  Copy,
} from 'lucide-react';
import type { StatusMapping, StatusRule, EmailTemplate, EmailTemplateType, EmailTemplatePreview } from '../types';
import { Layout } from '../components/layout';
import { Modal } from '../components/ui';
import { statusMappingsApi, connectionsApi, odataServicesApi, dockerApi, emailTemplatesApi } from '../api/client';
import { EnvironmentSettings } from '../components/settings/EnvironmentSettings';
import { SyncServicesSettings } from '../components/settings/SyncServicesSettings';
import { useAppSettings } from '../contexts';
import type { AppTheme, AlertThreshold } from '../contexts';

type SettingsTab = 'general' | 'connections' | 'sync-services' | 'odata-services' | 'mappings' | 'notifications' | 'email-templates' | 'environment';

const VALID_TABS: SettingsTab[] = ['general', 'connections', 'sync-services', 'odata-services', 'mappings', 'notifications', 'email-templates', 'environment'];

export function SettingsPage() {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<SettingsTab>('general');

  // Support deep-linking via ?tab=mappings (used by /status-mappings redirect)
  useEffect(() => {
    const tab = searchParams.get('tab') as SettingsTab | null;
    if (tab && VALID_TABS.includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const tabs = [
    { id: 'general' as const, label: 'General', icon: Settings },
    { id: 'connections' as const, label: 'Connections', icon: Database },
    { id: 'sync-services' as const, label: 'Sync Services', icon: ArrowLeftRight },
    { id: 'odata-services' as const, label: 'OData Services', icon: Package },
    { id: 'mappings' as const, label: 'Status Mappings', icon: Palette },
    { id: 'notifications' as const, label: 'Notifications', icon: Bell },
    { id: 'email-templates' as const, label: 'Email Templates', icon: Mail },
    { id: 'environment' as const, label: 'Environment & AI', icon: Brain },
  ];

  return (
    <Layout title="Settings">
      <div className="flex h-full">
        {/* Sidebar */}
        <div className="w-64 border-r border-gray-200 bg-white p-4">
          <nav className="space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-3 w-full px-3 py-2 text-sm rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {activeTab === 'general' && <GeneralSettings />}
          {activeTab === 'connections' && <ConnectionSettings />}
          {activeTab === 'sync-services' && <SyncServicesSettings />}
          {activeTab === 'odata-services' && <ODataServicesSettings />}
          {activeTab === 'mappings' && <StatusMappingSettings />}
          {activeTab === 'notifications' && <NotificationSettings />}
          {activeTab === 'email-templates' && <EmailTemplateSettings />}
          {activeTab === 'environment' && <EnvironmentSettings />}
        </div>
      </div>
    </Layout>
  );
}

function GeneralSettings() {
  const { appName, logoDataUrl, refreshInterval, theme, updateSettings } = useAppSettings();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Local form state — initialised from context
  const [formName, setFormName] = useState(appName);
  const [formInterval, setFormInterval] = useState(refreshInterval);
  const [formTheme, setFormTheme] = useState<AppTheme>(theme);
  const [formLogo, setFormLogo] = useState<string | null>(logoDataUrl);
  const [saved, setSaved] = useState(false);

  // Logo upload handler — reads file → base64 data URL
  const handleLogoUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Max 512 KB
    if (file.size > 512 * 1024) {
      alert('Logo file must be smaller than 512 KB.');
      return;
    }
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file (PNG, SVG, JPG).');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setFormLogo(reader.result as string);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleRemoveLogo = useCallback(() => {
    setFormLogo(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleSave = useCallback(() => {
    updateSettings({
      appName: formName.trim() || 'R4S OmniMens',
      logoDataUrl: formLogo,
      refreshInterval: Math.max(10, Math.min(3600, formInterval)),
      theme: formTheme,
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }, [formName, formLogo, formInterval, formTheme, updateSettings]);

  const themeOptions: { value: AppTheme; label: string; icon: React.ElementType }[] = [
    { value: 'light', label: 'Light', icon: Sun },
    { value: 'dark', label: 'Dark', icon: Moon },
    { value: 'system', label: 'System', icon: Monitor },
  ];

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-6">
        General Settings
      </h2>

      <div className="space-y-6 max-w-2xl">
        {/* Logo Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Application Logo
          </label>
          <div className="flex items-center gap-4">
            {/* Preview */}
            <div className="w-16 h-16 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden bg-gray-50">
              {formLogo ? (
                <img src={formLogo} alt="Logo" className="w-full h-full object-contain p-1" />
              ) : (
                <svg viewBox="0 0 79.73 100" className="w-8 h-8">
                  <path fill="#45bdd6" d="M39.71 0L0 99.94l55.6-29.78L39.71 0z"/>
                  <path fill="#674EA7" d="M40.02.06L79.73 100l-55.6-29.78L40.02.06z"/>
                  <path fill="#43434f" d="M39.87.7L24.13 70.22l15.68 8.4 15.79-8.46L39.87.7z"/>
                </svg>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                onChange={handleLogoUpload}
                className="hidden"
                id="logo-upload"
              />
              <div className="flex items-center gap-2">
                <label
                  htmlFor="logo-upload"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#45bdd6] bg-[#45bdd6]/10 border border-[#45bdd6]/30 rounded-lg cursor-pointer hover:bg-[#45bdd6]/20 transition-colors"
                >
                  <Upload className="w-3.5 h-3.5" />
                  Upload Logo
                </label>
                {formLogo && (
                  <button
                    onClick={handleRemoveLogo}
                    className="flex items-center gap-1 px-2.5 py-1.5 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                    Remove
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-400">PNG, SVG, JPG — max 512 KB. Displayed in the sidebar.</p>
            </div>
          </div>
        </div>

        {/* Application Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Application Name
          </label>
          <input
            type="text"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            placeholder="R4S OmniMens"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-[#45bdd6]"
          />
          <p className="text-xs text-gray-400 mt-1">Displayed next to the logo in the sidebar</p>
        </div>

        {/* Default Refresh Interval */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Default Refresh Interval (seconds)
          </label>
          <input
            type="number"
            value={formInterval}
            onChange={(e) => setFormInterval(parseInt(e.target.value) || 60)}
            min={10}
            max={3600}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-[#45bdd6]"
          />
          <p className="text-xs text-gray-400 mt-1">
            How often dashboards auto-refresh data (10–3600 seconds). Applied to all dashboards that use the default interval.
          </p>
        </div>

        {/* Theme */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Theme
          </label>
          <div className="flex gap-3">
            {themeOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setFormTheme(opt.value)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                  formTheme === opt.value
                    ? 'bg-[#45bdd6] text-white border-[#45bdd6] shadow-sm'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >
                <opt.icon className="w-4 h-4" />
                {opt.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-1">
            "System" follows your operating system preference
          </p>
        </div>

        {/* Save Button */}
        <div className="pt-4 flex items-center gap-3">
          <button
            onClick={handleSave}
            className="flex items-center gap-2 px-5 py-2 bg-[#45bdd6] text-white rounded-lg hover:bg-[#3aa8bf] transition-colors font-medium"
          >
            <Save className="w-4 h-4" />
            Save Changes
          </button>
          {saved && (
            <span className="flex items-center gap-1.5 text-sm text-green-600 animate-pulse">
              <CheckCircle2 className="w-4 h-4" />
              Settings saved!
            </span>
          )}
        </div>
      </div>

      {/* Docker Service Management */}
      <div className="mt-10 pt-8 border-t border-gray-200">
        <DockerRestartPanel />
      </div>
    </div>
  );
}

function DockerRestartPanel() {
  const [restartStatus, setRestartStatus] = useState<{
    type: 'idle' | 'loading' | 'success' | 'error';
    message: string;
    services?: string[];
  }>({ type: 'idle', message: '' });

  const [selectedServices, setSelectedServices] = useState<Set<string>>(
    new Set(['backend', 'celery'])
  );

  const allServices = [
    { id: 'backend', label: 'Backend (Daphne)', description: 'Django API server' },
    { id: 'celery', label: 'Celery Worker', description: 'Async task processing' },
    { id: 'celery-beat', label: 'Celery Beat', description: 'Scheduled tasks' },
    { id: 'frontend', label: 'Frontend (Vite)', description: 'React dev server' },
    { id: 'nginx', label: 'Nginx', description: 'Reverse proxy' },
  ];

  const toggleService = (id: string) => {
    setSelectedServices((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleRestart = async () => {
    const services = Array.from(selectedServices);
    if (services.length === 0) return;

    setRestartStatus({ type: 'loading', message: `Restarting ${services.join(', ')}...`, services });

    try {
      const result = await dockerApi.restart(services);
      setRestartStatus({
        type: 'success',
        message: result.message || `Successfully triggered restart for: ${services.join(', ')}`,
        services,
      });
      // Auto-clear success message after 10s
      setTimeout(() => setRestartStatus({ type: 'idle', message: '' }), 10000);
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      setRestartStatus({
        type: 'error',
        message: `Restart failed: ${errorMsg}`,
        services,
      });
    }
  };

  const handleQuickRestart = async () => {
    setSelectedServices(new Set(['backend', 'celery']));
    setRestartStatus({ type: 'loading', message: 'Quick restart: backend + celery...', services: ['backend', 'celery'] });

    try {
      const result = await dockerApi.restart(['backend', 'celery']);
      setRestartStatus({
        type: 'success',
        message: result.message || 'Backend + Celery restart triggered successfully',
        services: ['backend', 'celery'],
      });
      setTimeout(() => setRestartStatus({ type: 'idle', message: '' }), 10000);
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      setRestartStatus({
        type: 'error',
        message: `Quick restart failed: ${errorMsg}`,
      });
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-orange-50 rounded-lg">
          <Server className="w-5 h-5 text-orange-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Docker Service Management</h3>
          <p className="text-sm text-gray-500">
            Restart Docker services after code changes — also usable by Cowork Agent via browser
          </p>
        </div>
      </div>

      {/* Quick Restart Button */}
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-900">Quick Restart</p>
            <p className="text-sm text-gray-500">Restart Backend + Celery (most common after code changes)</p>
          </div>
          <button
            onClick={handleQuickRestart}
            disabled={restartStatus.type === 'loading'}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            id="docker-quick-restart-btn"
          >
            {restartStatus.type === 'loading' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RotateCcw className="w-4 h-4" />
            )}
            Restart Backend + Celery
          </button>
        </div>
      </div>

      {/* Service Selection */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
        <p className="text-sm font-medium text-gray-700 mb-3">Select services to restart:</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {allServices.map((svc) => (
            <label
              key={svc.id}
              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                selectedServices.has(svc.id)
                  ? 'border-orange-300 bg-orange-50'
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <input
                type="checkbox"
                checked={selectedServices.has(svc.id)}
                onChange={() => toggleService(svc.id)}
                className="w-4 h-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
              />
              <div>
                <p className="text-sm font-medium text-gray-900">{svc.label}</p>
                <p className="text-xs text-gray-500">{svc.description}</p>
              </div>
            </label>
          ))}
        </div>

        <div className="mt-4 flex items-center gap-3">
          <button
            onClick={handleRestart}
            disabled={selectedServices.size === 0 || restartStatus.type === 'loading'}
            className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            id="docker-custom-restart-btn"
          >
            {restartStatus.type === 'loading' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RotateCcw className="w-4 h-4" />
            )}
            Restart Selected ({selectedServices.size})
          </button>
          <span className="text-xs text-gray-400">
            Each service takes up to 30 seconds to restart
          </span>
        </div>
      </div>

      {/* Status Message */}
      {restartStatus.type !== 'idle' && (
        <div
          className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
            restartStatus.type === 'loading'
              ? 'bg-blue-50 text-blue-700'
              : restartStatus.type === 'success'
                ? 'bg-green-50 text-green-700'
                : 'bg-red-50 text-red-700'
          }`}
        >
          {restartStatus.type === 'loading' && <Loader2 className="w-4 h-4 animate-spin" />}
          {restartStatus.type === 'success' && <CheckCircle2 className="w-4 h-4" />}
          {restartStatus.type === 'error' && <AlertCircle className="w-4 h-4" />}
          {restartStatus.message}
        </div>
      )}
    </div>
  );
}

function ConnectionSettings() {
  const { data: connections, isLoading } = useQuery({
    queryKey: ['connections'],
    queryFn: connectionsApi.list,
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          API Connections
        </h2>
        <button className="flex items-center gap-2 px-4 py-2 bg-[#45bdd6] text-white rounded-lg hover:bg-[#3aa8bf]">
          <Database className="w-4 h-4" />
          Add Connection
        </button>
      </div>

      {isLoading ? (
        <div className="animate-pulse space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-20 bg-gray-100 rounded-lg" />
          ))}
        </div>
      ) : connections?.length === 0 ? (
        <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <Globe className="w-10 h-10 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">No connections configured</p>
        </div>
      ) : (
        <div className="space-y-4">
          {connections?.map((connection) => (
            <div
              key={connection.id}
              className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between"
            >
              <div className="flex items-center gap-4">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <Globe className="w-5 h-5 text-[#45bdd6]" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{connection.name}</p>
                  <p className="text-sm text-gray-500">{connection.base_url}</p>
                </div>
              </div>
              <span
                className={`px-2 py-1 rounded text-xs font-medium ${
                  connection.is_active
                    ? 'bg-green-50 text-green-700'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {connection.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Preset colour palette for quick rule colour picks ───
const RULE_COLOR_PRESETS = [
  '#22c55e', // green
  '#3b82f6', // blue
  '#eab308', // yellow
  '#f97316', // orange
  '#ef4444', // red
  '#8b5cf6', // purple
  '#ec4899', // pink
  '#9ca3af', // gray
  '#06b6d4', // cyan
  '#6b7280', // dark-gray
];

// ─── Lucide icon names commonly used for statuses ───
const ICON_OPTIONS = [
  'check', 'check-circle', 'circle', 'clock', 'play', 'pause',
  'alert-triangle', 'alert-circle', 'x-circle', 'minus',
  'chevron-up', 'chevron-down', 'archive', 'file', 'rocket',
  'package', 'coffee', 'help-circle', 'calendar', 'zap',
];

// ─── Empty rule template ───
function emptyRule(): StatusRule {
  return { condition: '', color: '#9ca3af', icon: 'circle', label: '' };
}

// ─── Rule row inside the modal ───
function RuleRow({
  rule,
  index,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
}: {
  rule: StatusRule;
  index: number;
  onChange: (idx: number, r: StatusRule) => void;
  onRemove: (idx: number) => void;
  onMoveUp: (idx: number) => void;
  onMoveDown: (idx: number) => void;
  isFirst: boolean;
  isLast: boolean;
}) {
  return (
    <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200 group">
      {/* Reorder */}
      <div className="flex flex-col gap-0.5 pt-1">
        <button
          type="button"
          disabled={isFirst}
          onClick={() => onMoveUp(index)}
          className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
        >
          <ChevronUp className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          disabled={isLast}
          onClick={() => onMoveDown(index)}
          className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
        >
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Fields */}
      <div className="flex-1 grid grid-cols-12 gap-2">
        {/* Condition — spans 4 cols */}
        <div className="col-span-4">
          <label className="block text-[10px] uppercase text-gray-500 mb-0.5">Condition</label>
          <input
            type="text"
            value={rule.condition ?? ''}
            onChange={(e) => onChange(index, { ...rule, condition: e.target.value })}
            placeholder="value == 'Active'"
            className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs font-mono focus:ring-1 focus:ring-[#45bdd6]/40 focus:border-[#45bdd6]"
          />
        </div>

        {/* Label — spans 3 cols */}
        <div className="col-span-3">
          <label className="block text-[10px] uppercase text-gray-500 mb-0.5">Label</label>
          <input
            type="text"
            value={rule.label}
            onChange={(e) => onChange(index, { ...rule, label: e.target.value })}
            placeholder="Active"
            className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-[#45bdd6]/40 focus:border-[#45bdd6]"
          />
        </div>

        {/* Colour — spans 2 cols */}
        <div className="col-span-2">
          <label className="block text-[10px] uppercase text-gray-500 mb-0.5">Colour</label>
          <div className="flex items-center gap-1">
            <input
              type="color"
              value={rule.color}
              onChange={(e) => onChange(index, { ...rule, color: e.target.value })}
              className="w-7 h-7 border border-gray-300 rounded cursor-pointer p-0"
            />
            <input
              type="text"
              value={rule.color}
              onChange={(e) => onChange(index, { ...rule, color: e.target.value })}
              className="w-full px-1.5 py-1.5 border border-gray-300 rounded text-[10px] font-mono focus:ring-1 focus:ring-[#45bdd6]/40 focus:border-[#45bdd6]"
            />
          </div>
        </div>

        {/* Icon — spans 2 cols */}
        <div className="col-span-2">
          <label className="block text-[10px] uppercase text-gray-500 mb-0.5">Icon</label>
          <select
            value={rule.icon}
            onChange={(e) => onChange(index, { ...rule, icon: e.target.value })}
            className="w-full px-1.5 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-[#45bdd6]/40 focus:border-[#45bdd6]"
          >
            {ICON_OPTIONS.map((ic) => (
              <option key={ic} value={ic}>{ic}</option>
            ))}
          </select>
        </div>

        {/* Preview chip — spans 1 col */}
        <div className="col-span-1 flex items-end pb-0.5 justify-center">
          <span
            className="inline-flex items-center gap-1 px-1.5 py-1 rounded text-[10px] font-medium whitespace-nowrap"
            style={{ backgroundColor: `${rule.color}20`, color: rule.color }}
          >
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: rule.color }} />
            {rule.label || '…'}
          </span>
        </div>
      </div>

      {/* Delete */}
      <button
        type="button"
        onClick={() => onRemove(index)}
        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors mt-4"
        title="Remove rule"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ─── StatusMappingSettings (full CRUD + Rule-Editor) ───

function StatusMappingSettings() {
  const queryClient = useQueryClient();

  const { data: mappings, isLoading, isError } = useQuery({
    queryKey: ['statusMappings'],
    queryFn: statusMappingsApi.list,
  });

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);

  // Form fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sourceField, setSourceField] = useState('');
  const [defaultColor, setDefaultColor] = useState('#9ca3af');
  const [defaultIcon, setDefaultIcon] = useState('help-circle');
  const [defaultLabel, setDefaultLabel] = useState('Unknown');
  const [rules, setRules] = useState<StatusRule[]>([]);

  // Save feedback
  const [saved, setSaved] = useState(false);

  // ─── Mutations ───
  const createMutation = useMutation({
    mutationFn: (data: Partial<StatusMapping>) => statusMappingsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['statusMappings'] });
      closeModal();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<StatusMapping> }) =>
      statusMappingsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['statusMappings'] });
      closeModal();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => statusMappingsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['statusMappings'] });
    },
  });

  // ─── Helpers ───
  const resetForm = () => {
    setEditId(null);
    setName('');
    setDescription('');
    setSourceField('');
    setDefaultColor('#9ca3af');
    setDefaultIcon('help-circle');
    setDefaultLabel('Unknown');
    setRules([]);
  };

  const closeModal = () => {
    setModalOpen(false);
    resetForm();
  };

  const openCreate = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEdit = (m: StatusMapping) => {
    setEditId(m.id);
    setName(m.name);
    setDescription(m.description);
    setSourceField(m.source_field);
    setDefaultColor(m.default_color);
    setDefaultIcon(m.default_icon);
    setDefaultLabel(m.default_label);
    setRules(m.rules.map((r) => ({ ...r })));
    setModalOpen(true);
  };

  const handleDelete = (m: StatusMapping) => {
    if (confirm(`Delete mapping "${m.name}"? This cannot be undone.`)) {
      deleteMutation.mutate(m.id);
    }
  };

  const handleDuplicate = (m: StatusMapping) => {
    resetForm();
    setName(`${m.name} (Copy)`);
    setDescription(m.description);
    setSourceField(m.source_field);
    setDefaultColor(m.default_color);
    setDefaultIcon(m.default_icon);
    setDefaultLabel(m.default_label);
    setRules(m.rules.map((r) => ({ ...r })));
    setModalOpen(true);
  };

  // Rule CRUD
  const addRule = () => setRules([...rules, emptyRule()]);
  const removeRule = (idx: number) => setRules(rules.filter((_, i) => i !== idx));
  const changeRule = (idx: number, r: StatusRule) =>
    setRules(rules.map((old, i) => (i === idx ? r : old)));
  const moveRuleUp = (idx: number) => {
    if (idx === 0) return;
    const next = [...rules];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    setRules(next);
  };
  const moveRuleDown = (idx: number) => {
    if (idx >= rules.length - 1) return;
    const next = [...rules];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    setRules(next);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload: Partial<StatusMapping> = {
      name,
      description,
      source_field: sourceField,
      default_color: defaultColor,
      default_icon: defaultIcon,
      default_label: defaultLabel,
      rules,
    };

    if (editId !== null) {
      updateMutation.mutate({ id: editId, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Status Mappings</h2>
          <p className="text-sm text-gray-500 mt-1">
            Define how ERP status values are visualised with colours, icons and labels.
            Works with any ERP system (SAP, Infor, Oracle, Microsoft Dynamics, etc.).
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-[#45bdd6] text-white rounded-lg hover:bg-[#3ba8c0] text-sm font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Mapping
        </button>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="animate-pulse space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-28 bg-gray-100 rounded-lg" />
          ))}
        </div>
      )}

      {/* Error */}
      {isError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
          <p className="text-sm text-red-700">Failed to load status mappings. Check backend connectivity.</p>
        </div>
      )}

      {/* Empty */}
      {!isLoading && !isError && mappings?.length === 0 && (
        <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl p-10 text-center">
          <Palette className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No status mappings yet</h3>
          <p className="text-sm text-gray-500 mb-4">
            Status mappings translate raw ERP status codes into meaningful colours and labels for your dashboards.
          </p>
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#45bdd6] text-white rounded-lg hover:bg-[#3ba8c0] text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            Create First Mapping
          </button>
        </div>
      )}

      {/* Mapping Cards */}
      {!isLoading && !isError && mappings && mappings.length > 0 && (
        <div className="space-y-4">
          {mappings.map((mapping) => (
            <div
              key={mapping.id}
              className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-sm transition-shadow"
            >
              {/* Top row */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${mapping.default_color}20` }}
                  >
                    <Palette className="w-5 h-5" style={{ color: mapping.default_color }} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{mapping.name}</h3>
                    {mapping.description && (
                      <p className="text-xs text-gray-500 mt-0.5">{mapping.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <code className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded font-mono mr-2">
                    {mapping.source_field}
                  </code>
                  <button
                    onClick={() => handleDuplicate(mapping)}
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                    title="Duplicate"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => openEdit(mapping)}
                    className="p-1.5 text-gray-400 hover:text-[#45bdd6] hover:bg-[#45bdd6]/10 rounded transition-colors"
                    title="Edit"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(mapping)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Rule chips */}
              <div className="flex flex-wrap gap-1.5">
                {mapping.rules.slice(0, 8).map((rule, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium"
                    style={{ backgroundColor: `${rule.color}20`, color: rule.color }}
                  >
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: rule.color }} />
                    {rule.label}
                  </span>
                ))}
                {mapping.rules.length > 8 && (
                  <span className="text-xs text-gray-400 self-center">
                    +{mapping.rules.length - 8} more
                  </span>
                )}
                {mapping.rules.length === 0 && (
                  <span className="text-xs text-gray-400 italic">No rules defined</span>
                )}
                {/* Default chip */}
                <span
                  className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs border border-dashed"
                  style={{ borderColor: mapping.default_color, color: mapping.default_color }}
                >
                  default: {mapping.default_label}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-blue-800">How Conditions Work</p>
          <p className="text-xs text-blue-600 mt-1">
            Rules are evaluated top to bottom — the first matching rule wins.
            Use Python-style expressions such as{' '}
            <code className="bg-blue-100 px-1 rounded">value == 'Active'</code>,{' '}
            <code className="bg-blue-100 px-1 rounded">value &gt;= 50</code>, or{' '}
            <code className="bg-blue-100 px-1 rounded">value == 95 and reason == 'MATERIAL'</code>.
            The variable <code className="bg-blue-100 px-1 rounded">value</code> refers to the
            source field value at runtime.
          </p>
        </div>
      </div>

      {/* ─── Create / Edit Modal ─── */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editId !== null ? 'Edit Status Mapping' : 'New Status Mapping'}
        size="xl"
      >
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Basic fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Operation Status"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#45bdd6]/20 focus:border-[#45bdd6]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Source Field *</label>
              <input
                type="text"
                value={sourceField}
                onChange={(e) => setSourceField(e.target.value)}
                placeholder="e.g. Status or OperationStatus"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-[#45bdd6]/20 focus:border-[#45bdd6]"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of what this mapping visualises"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#45bdd6]/20 focus:border-[#45bdd6]"
            />
          </div>

          {/* Default styling */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Default (when no rule matches)</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={defaultColor}
                onChange={(e) => setDefaultColor(e.target.value)}
                className="w-8 h-8 border border-gray-300 rounded cursor-pointer p-0"
              />
              <input
                type="text"
                value={defaultColor}
                onChange={(e) => setDefaultColor(e.target.value)}
                className="w-24 px-2 py-1.5 border border-gray-300 rounded text-xs font-mono focus:ring-1 focus:ring-[#45bdd6]/40"
              />
              <select
                value={defaultIcon}
                onChange={(e) => setDefaultIcon(e.target.value)}
                className="px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-[#45bdd6]/40"
              >
                {ICON_OPTIONS.map((ic) => (
                  <option key={ic} value={ic}>{ic}</option>
                ))}
              </select>
              <input
                type="text"
                value={defaultLabel}
                onChange={(e) => setDefaultLabel(e.target.value)}
                placeholder="Unknown"
                className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-[#45bdd6]/40"
              />
              <span
                className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium whitespace-nowrap border border-dashed"
                style={{ borderColor: defaultColor, color: defaultColor }}
              >
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: defaultColor }} />
                {defaultLabel || '…'}
              </span>
            </div>
          </div>

          {/* ─── Rule Editor ─── */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">
                Rules ({rules.length})
              </label>
              <button
                type="button"
                onClick={addRule}
                className="inline-flex items-center gap-1 text-xs text-[#45bdd6] hover:text-[#3ba8c0] font-medium"
              >
                <Plus className="w-3.5 h-3.5" />
                Add Rule
              </button>
            </div>

            {rules.length === 0 ? (
              <div className="bg-gray-50 border border-dashed border-gray-300 rounded-lg p-4 text-center">
                <p className="text-xs text-gray-500">
                  No rules yet. Click "Add Rule" to define how specific values map to colours and labels.
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {rules.map((rule, idx) => (
                  <RuleRow
                    key={idx}
                    rule={rule}
                    index={idx}
                    onChange={changeRule}
                    onRemove={removeRule}
                    onMoveUp={moveRuleUp}
                    onMoveDown={moveRuleDown}
                    isFirst={idx === 0}
                    isLast={idx === rules.length - 1}
                  />
                ))}
              </div>
            )}

            {/* Quick-add presets */}
            {rules.length > 0 && (
              <div className="flex items-center gap-2 mt-2">
                <span className="text-[10px] text-gray-400 uppercase">Quick colour:</span>
                {RULE_COLOR_PRESETS.slice(0, 8).map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => {
                      const last = rules.length - 1;
                      changeRule(last, { ...rules[last], color: c });
                    }}
                    className="w-4 h-4 rounded-full border border-gray-200 hover:scale-125 transition-transform"
                    style={{ backgroundColor: c }}
                    title={c}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Error */}
          {(createMutation.isError || updateMutation.isError) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5" />
              <p className="text-sm text-red-700">Error saving mapping. Check inputs and backend.</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2 border-t border-gray-200">
            <button
              type="button"
              onClick={closeModal}
              className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                isPending
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-[#45bdd6] text-white hover:bg-[#3ba8c0]'
              }`}
            >
              {isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {editId !== null ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function ODataServicesSettings() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedConnectionId, setSelectedConnectionId] = useState<string>('');
  const [uploadStatus, setUploadStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  // Fetch connections for dropdown
  const { data: connections } = useQuery({
    queryKey: ['connections'],
    queryFn: connectionsApi.list,
  });

  // Fetch OData service definitions
  const { data: services, isLoading } = useQuery({
    queryKey: ['odata-services'],
    queryFn: () => odataServicesApi.list(),
  });

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: ({ connectionId, file }: { connectionId: string; file: File }) =>
      odataServicesApi.upload(connectionId, file),
    onSuccess: (data) => {
      setUploadStatus({
        type: 'success',
        message: `Service "${data.namespace}" successfully ${data.created ? 'imported' : 'updated'}! ${data.entity_count} Entities found.`,
      });
      queryClient.invalidateQueries({ queryKey: ['odata-services'] });
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    onError: (error: Error) => {
      setUploadStatus({
        type: 'error',
        message: `Error: ${error.message}`,
      });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => odataServicesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['odata-services'] });
    },
  });

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedConnectionId) return;

    setUploadStatus({ type: null, message: '' });
    uploadMutation.mutate({ connectionId: selectedConnectionId, file });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            OData Service Definitions
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Import *.odata-svc.json files from Infor API Gateway for correct Navigation Properties
          </p>
        </div>
      </div>

      {/* Upload Section */}
      <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200 rounded-lg p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-white rounded-lg shadow-sm">
            <Upload className="w-6 h-6 text-cyan-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-gray-900 mb-2">
              Import Service Definition
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Export the OpenAPI Spec from Infor OS → API Gateway → Service → Export OpenAPI Spec
            </p>

            <div className="flex flex-wrap items-end gap-4">
              {/* Connection Select */}
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Connection
                </label>
                <select
                  value={selectedConnectionId}
                  onChange={(e) => setSelectedConnectionId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                >
                  <option value="">-- Select Connection --</option>
                  {connections?.map((conn) => (
                    <option key={conn.id} value={conn.id}>
                      {conn.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* File Upload */}
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleFileUpload}
                  disabled={!selectedConnectionId || uploadMutation.isPending}
                  className="hidden"
                  id="odata-file-upload"
                />
                <label
                  htmlFor="odata-file-upload"
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-colors ${
                    selectedConnectionId && !uploadMutation.isPending
                      ? 'bg-cyan-600 text-white hover:bg-cyan-700'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <FileJson className="w-4 h-4" />
                  {uploadMutation.isPending ? 'Importing...' : 'Select *.odata-svc.json'}
                </label>
              </div>
            </div>

            {/* Status Message */}
            {uploadStatus.type && (
              <div
                className={`mt-4 flex items-center gap-2 text-sm ${
                  uploadStatus.type === 'success' ? 'text-green-700' : 'text-red-700'
                }`}
              >
                {uploadStatus.type === 'success' ? (
                  <CheckCircle2 className="w-4 h-4" />
                ) : (
                  <AlertCircle className="w-4 h-4" />
                )}
                {uploadStatus.message}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Services List */}
      <h3 className="font-medium text-gray-900 mb-3">Imported Services</h3>

      {isLoading ? (
        <div className="animate-pulse space-y-4">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="h-24 bg-gray-100 rounded-lg" />
          ))}
        </div>
      ) : !services || services.length === 0 ? (
        <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <Package className="w-10 h-10 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">No Service Definitions imported</p>
          <p className="text-sm text-gray-400 mt-1">
            Import a *.odata-svc.json file to automatically detect Navigation Properties
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {services.map((service) => (
            <div
              key={service.id}
              className="bg-white border border-gray-200 rounded-lg p-4"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-cyan-50 rounded-lg mt-0.5">
                    <Package className="w-5 h-5 text-cyan-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{service.namespace}</p>
                    {service.title && (
                      <p className="text-sm text-gray-500">{service.title}</p>
                    )}
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <Link2 className="w-3 h-3" />
                        {service.connection_name}
                      </span>
                      <span>{service.entity_count} Entities</span>
                      <span>
                        Imported: {new Date(service.uploaded_at).toLocaleDateString('en-US')}
                      </span>
                    </div>
                    {/* Entity Pills */}
                    {service.parsed_entities && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {Object.keys(service.parsed_entities).slice(0, 6).map((entityName) => (
                          <span
                            key={entityName}
                            className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded"
                          >
                            {entityName}
                          </span>
                        ))}
                        {Object.keys(service.parsed_entities).length > 6 && (
                          <span className="px-2 py-0.5 text-gray-400 text-xs">
                            +{Object.keys(service.parsed_entities).length - 6} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => {
                    if (confirm(`Really delete service "${service.namespace}"?`)) {
                      deleteMutation.mutate(service.id);
                    }
                  }}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NotificationSettings() {
  const { browserNotifications, soundAlerts, alertThreshold, updateSettings } = useAppSettings();

  const [localBrowser, setLocalBrowser] = useState(browserNotifications);
  const [localSound, setLocalSound] = useState(soundAlerts);
  const [localThreshold, setLocalThreshold] = useState<AlertThreshold>(alertThreshold);
  const [saved, setSaved] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);

  const handleBrowserToggle = async (checked: boolean) => {
    if (checked && 'Notification' in window) {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        setPermissionDenied(true);
        setLocalBrowser(false);
        return;
      }
      setPermissionDenied(false);
    }
    setLocalBrowser(checked);
  };

  const handleTestSound = () => {
    try {
      const ctx = new AudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 660;
      gain.gain.value = 0.15;
      osc.start();
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      osc.stop(ctx.currentTime + 0.3);
    } catch {
      // AudioContext not available
    }
  };

  const handleSave = () => {
    updateSettings({
      browserNotifications: localBrowser,
      soundAlerts: localSound,
      alertThreshold: localThreshold,
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const thresholdOptions: { value: AlertThreshold; label: string; icon: typeof Info; desc: string }[] = [
    { value: 'all', label: 'All Events', icon: Info, desc: 'Receive all notifications including info, warnings, and errors' },
    { value: 'warning', label: 'Warnings & Errors', icon: AlertTriangle, desc: 'Only receive warning and error notifications' },
    { value: 'error', label: 'Errors Only', icon: XCircle, desc: 'Only receive critical error notifications' },
  ];

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-6">
        Notification Settings
      </h2>

      <div className="space-y-6 max-w-2xl">
        {/* Browser Notifications */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-50 rounded-lg">
                <BellRing className="w-5 h-5 text-[#45bdd6]" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Browser Notifications</p>
                <p className="text-sm text-gray-500">
                  Show desktop notifications for planning events
                </p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={localBrowser}
                onChange={(e) => handleBrowserToggle(e.target.checked)}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#45bdd6]" />
            </label>
          </div>
          {permissionDenied && (
            <div className="mt-3 flex items-center gap-2 text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              Browser notification permission was denied. Please enable it in your browser settings.
            </div>
          )}
        </div>

        {/* Sound Alerts */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-50 rounded-lg">
                {localSound ? (
                  <Volume2 className="w-5 h-5 text-[#45bdd6]" />
                ) : (
                  <VolumeX className="w-5 h-5 text-gray-400" />
                )}
              </div>
              <div>
                <p className="font-medium text-gray-900">Sound Alerts</p>
                <p className="text-sm text-gray-500">
                  Play a notification sound for incoming alerts
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {localSound && (
                <button
                  onClick={handleTestSound}
                  className="text-xs text-[#45bdd6] hover:underline"
                >
                  Test
                </button>
              )}
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={localSound}
                  onChange={(e) => setLocalSound(e.target.checked)}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#45bdd6]" />
              </label>
            </div>
          </div>
        </div>

        {/* Alert Threshold */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-cyan-50 rounded-lg">
              <Shield className="w-5 h-5 text-[#45bdd6]" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Alert Threshold</p>
              <p className="text-sm text-gray-500">
                Filter which notification levels are shown
              </p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {thresholdOptions.map((opt) => {
              const Icon = opt.icon;
              const isActive = localThreshold === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => setLocalThreshold(opt.value)}
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all text-sm ${
                    isActive
                      ? 'border-[#45bdd6] bg-cyan-50 text-[#45bdd6]'
                      : 'border-gray-200 hover:border-gray-300 text-gray-600'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="font-medium">{opt.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Email Notifications Info */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Mail className="w-5 h-5 text-gray-500" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Email Notifications</p>
              <p className="text-sm text-gray-500">
                Email settings are managed via Environment Variables (category: Email / SMTP).
                Configure <code className="text-xs bg-gray-200 px-1 py-0.5 rounded">PLANNING_NOTIFICATION_ENABLED</code> and <code className="text-xs bg-gray-200 px-1 py-0.5 rounded">PLANNING_NOTIFICATION_RECIPIENTS</code> in the Environment & AI tab.
              </p>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="pt-2">
          <button
            onClick={handleSave}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-white font-medium transition-all ${
              saved
                ? 'bg-emerald-500'
                : 'bg-[#45bdd6] hover:bg-[#3aa8bf]'
            }`}
          >
            {saved ? (
              <CheckCircle2 className="w-4 h-4" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {saved ? 'Saved!' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ============ Email Template Settings ============

const TEMPLATE_TYPE_OPTIONS: { value: EmailTemplateType; label: string; color: string }[] = [
  { value: 'scenario_success', label: 'Scenario Success', color: '#16a34a' },
  { value: 'scenario_failure', label: 'Scenario Failure', color: '#dc2626' },
  { value: 'writeback_success', label: 'Write-Back Success', color: '#0284c7' },
  { value: 'writeback_failure', label: 'Write-Back Failure', color: '#dc2626' },
  { value: 'custom', label: 'Custom', color: '#6b7280' },
];

const emptyTemplate = (): Partial<EmailTemplate> => ({
  name: '',
  description: '',
  template_type: 'custom',
  subject_template: '',
  html_body: '',
  primary_color: '#45bdd6',
  is_default: false,
  is_active: true,
});

function EmailTemplateSettings() {
  const queryClient = useQueryClient();

  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState<Partial<EmailTemplate>>(emptyTemplate());
  const [editId, setEditId] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewSubject, setPreviewSubject] = useState<string>('');
  const [testEmail, setTestEmail] = useState('');
  const [testMsg, setTestMsg] = useState('');

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['email-templates'],
    queryFn: emailTemplatesApi.list,
  });

  const saveMutation = useMutation({
    mutationFn: (data: Partial<EmailTemplate>) =>
      editId
        ? emailTemplatesApi.update(editId, data)
        : emailTemplatesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      setModalOpen(false);
      setEditId(null);
      setEditData(emptyTemplate());
    },
  });

  const deleteMutation = useMutation({
    mutationFn: emailTemplatesApi.delete,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['email-templates'] }),
  });

  const openCreate = () => {
    setEditId(null);
    setEditData(emptyTemplate());
    setPreviewHtml(null);
    setPreviewSubject('');
    setTestMsg('');
    setModalOpen(true);
  };

  const openEdit = (tpl: EmailTemplate) => {
    setEditId(tpl.id);
    setEditData({ ...tpl });
    setPreviewHtml(null);
    setPreviewSubject('');
    setTestMsg('');
    setModalOpen(true);
  };

  const handlePreview = async () => {
    if (!editId) return;
    try {
      const result: EmailTemplatePreview = await emailTemplatesApi.preview(editId);
      setPreviewSubject(result.subject);
      setPreviewHtml(result.html);
    } catch {
      setPreviewHtml('<p style="color:red;">Preview failed. Save the template first.</p>');
    }
  };

  const handleTestSend = async () => {
    if (!editId || !testEmail) return;
    setTestMsg('Sending...');
    try {
      const result = await emailTemplatesApi.testSend(editId, testEmail);
      setTestMsg(result.message);
    } catch {
      setTestMsg('Failed to send test email. Check SMTP configuration.');
    }
  };

  const typeLabel = (type: EmailTemplateType) =>
    TEMPLATE_TYPE_OPTIONS.find((o) => o.value === type)?.label ?? type;

  const typeColor = (type: EmailTemplateType) =>
    TEMPLATE_TYPE_OPTIONS.find((o) => o.value === type)?.color ?? '#6b7280';

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Email Templates</h2>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-[#45bdd6] text-white rounded-lg hover:bg-[#3aa8bf] text-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          New Template
        </button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-blue-700">
            Email templates define the HTML layout for notification emails. When a scenario event occurs,
            the system uses the <strong>default</strong> template for that event type to render the email.
            Variables like <code className="bg-blue-100 px-1 rounded">{'{{ scenario_name }}'}</code> are
            replaced with actual values automatically. SMTP settings are configured in the Environment & AI tab.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : templates.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Mail className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="font-medium">No email templates yet</p>
          <p className="text-sm mt-1">
            Run <code className="bg-gray-100 px-1 rounded">python manage.py seed_data</code> to create defaults, or add one manually.
          </p>
        </div>
      ) : (
        <div className="space-y-3 max-w-3xl">
          {templates.map((tpl) => (
            <div
              key={tpl.id}
              className={`bg-white border rounded-lg p-4 ${
                tpl.is_active ? 'border-gray-200' : 'border-gray-200 opacity-50'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: tpl.primary_color }}
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{tpl.name}</span>
                      {tpl.is_default && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">
                          Default
                        </span>
                      )}
                      {!tpl.is_active && (
                        <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">
                          Inactive
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span
                        className="text-xs font-medium px-2 py-0.5 rounded-full text-white"
                        style={{ backgroundColor: typeColor(tpl.template_type) }}
                      >
                        {typeLabel(tpl.template_type)}
                      </span>
                      {tpl.description && (
                        <span className="text-xs text-gray-500 truncate max-w-[300px]">
                          {tpl.description}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openEdit(tpl)}
                    className="p-1.5 text-gray-400 hover:text-[#45bdd6] hover:bg-cyan-50 rounded"
                    title="Edit"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      if (confirm(`Delete template "${tpl.name}"?`)) {
                        deleteMutation.mutate(tpl.id);
                      }
                    }}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit Modal */}
      <Modal
        isOpen={modalOpen}
        title={editId ? 'Edit Email Template' : 'New Email Template'}
        onClose={() => { setModalOpen(false); setEditId(null); }}
        size="lg"
      >
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={editData.name || ''}
                onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 focus:border-blue-400"
                placeholder="e.g. Scenario Success"
              />
            </div>

            {/* Type + Primary Color */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={editData.template_type || 'custom'}
                  onChange={(e) => setEditData({ ...editData, template_type: e.target.value as EmailTemplateType })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 focus:border-blue-400"
                >
                  {TEMPLATE_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Primary Colour</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={editData.primary_color || '#45bdd6'}
                    onChange={(e) => setEditData({ ...editData, primary_color: e.target.value })}
                    className="w-10 h-10 p-0 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={editData.primary_color || '#45bdd6'}
                    onChange={(e) => setEditData({ ...editData, primary_color: e.target.value })}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                    maxLength={7}
                  />
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                value={editData.description || ''}
                onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="Short description of when this template is used"
              />
            </div>

            {/* Subject Template */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subject Line</label>
              <input
                type="text"
                value={editData.subject_template || ''}
                onChange={(e) => setEditData({ ...editData, subject_template: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                placeholder="[R4S] {{ scenario_name }} — {{ event_type }}"
              />
            </div>

            {/* HTML Body */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">HTML Body</label>
              <textarea
                value={editData.html_body || ''}
                onChange={(e) => setEditData({ ...editData, html_body: e.target.value })}
                rows={14}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono leading-relaxed"
                placeholder="<html>...</html>"
              />
            </div>

            {/* Flags */}
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editData.is_default || false}
                  onChange={(e) => setEditData({ ...editData, is_default: e.target.checked })}
                  className="rounded border-gray-300 text-[#45bdd6] focus:ring-[#45bdd6]"
                />
                <span className="text-sm text-gray-700">Default for this type</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editData.is_active ?? true}
                  onChange={(e) => setEditData({ ...editData, is_active: e.target.checked })}
                  className="rounded border-gray-300 text-[#45bdd6] focus:ring-[#45bdd6]"
                />
                <span className="text-sm text-gray-700">Active</span>
              </label>
            </div>

            {/* Preview Section (only for existing templates) */}
            {editId && (
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center gap-3 mb-3">
                  <button
                    onClick={handlePreview}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700"
                  >
                    <Globe className="w-4 h-4" />
                    Preview
                  </button>

                  <div className="flex items-center gap-2 flex-1">
                    <input
                      type="email"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                      placeholder="test@example.com"
                    />
                    <button
                      onClick={handleTestSend}
                      disabled={!testEmail}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[#45bdd6] hover:bg-[#3aa8bf] text-white rounded-lg disabled:opacity-50"
                    >
                      <Mail className="w-4 h-4" />
                      Test Send
                    </button>
                  </div>
                </div>
                {testMsg && (
                  <p className={`text-xs mb-3 ${testMsg.includes('Failed') ? 'text-red-500' : 'text-green-600'}`}>
                    {testMsg}
                  </p>
                )}
                {previewHtml && (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    {previewSubject && (
                      <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                        <span className="text-xs text-gray-500">Subject:</span>{' '}
                        <span className="text-sm font-medium text-gray-800">{previewSubject}</span>
                      </div>
                    )}
                    <div
                      className="bg-white"
                      style={{ maxHeight: '400px', overflow: 'auto' }}
                    >
                      <iframe
                        srcDoc={previewHtml}
                        title="Email Preview"
                        className="w-full border-0"
                        style={{ height: '380px' }}
                        sandbox=""
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
            <button
              onClick={() => { setModalOpen(false); setEditId(null); }}
              className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={() => saveMutation.mutate(editData)}
              disabled={saveMutation.isPending || !editData.name || !editData.subject_template}
              className="flex items-center gap-2 px-4 py-2 bg-[#45bdd6] text-white rounded-lg hover:bg-[#3aa8bf] text-sm font-medium disabled:opacity-50"
            >
              {saveMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {editId ? 'Update' : 'Create'}
            </button>
          </div>
        </Modal>
    </div>
  );
}
